import { usePatchArtifact, type FieldPatch } from "@/hooks/usePatchArtifact.ts";
import { createContext, useContext, useMemo, type ReactNode } from "react";

/**
 * Render context shared by every block in a server-driven `<ArtifactView>`.
 * Carries the IDs needed to scope a patch + a `patch`/`patchOne` pair the
 * blocks call to write back. `isReadOnly` collapses today's per-component
 * checks (status !== "waitingForUserAction" || isSharedThread) into a single
 * source of truth — but blocks should still respect their own `editable`
 * field, since the server can choose to disable an individual field even
 * when the rest of the card is editable.
 */
export type ArtifactViewCtx = {
  threadId: number;
  messageId: number;
  artifactId: string;
  isReadOnly: boolean;
  /** True on shared (unauthenticated) thread views — blocks suppress
   *  session-authenticated content (e.g. file previews) that would 401. */
  isSharedThread: boolean;
  /** Frame source for an applet the viewer does not own. Set on a shared or
   *  claimed artifact view, where the thread-scoped preview route would 401 (or
   *  worse, be assembled from sentinel ids). Its presence puts the applet in
   *  read-only mode — see `AppletPreview.sharedPreviewSrc`. */
  appletPreviewSrc?: string;
  /** True when the card is rendered at full size (lightbox), so blocks can
   *  opt into higher-resolution assets. */
  maximized: boolean;
  /** The view fills a host with a definite height, which a self-scrolling
   * block such as an applet frame should fill too. */
  fill: boolean;
  isPending: boolean;
  patch: (patches: FieldPatch[]) => Promise<unknown>;
  patchOne: (path: string, value: unknown) => Promise<unknown>;
  /** Close the surface hosting the view (the lightbox), when there is one. A
   *  block calls this after an action that ends the user's interaction with the
   *  card — e.g. handing a document off to the agent. Undefined when the view
   *  isn't in a closeable container (admin preview, sidebar). */
  onRequestClose?: () => void;
};

const ArtifactViewContext = createContext<ArtifactViewCtx | null>(null);

export const useArtifactViewCtx = (): ArtifactViewCtx => {
  const ctx = useContext(ArtifactViewContext);
  if (!ctx) throw new Error("useArtifactViewCtx must be used inside <ArtifactViewProvider>");
  return ctx;
};

/**
 * Optional patcher override. When provided, the provider uses these handlers
 * instead of the default `usePatchArtifact` → server POST flow. Used by the
 * admin preview tool to apply patches to local mock state without hitting
 * the API.
 */
export type ArtifactViewPatcherOverride = {
  patch: (patches: FieldPatch[]) => Promise<unknown>;
  patchOne: (path: string, value: unknown) => Promise<unknown>;
  isPending?: boolean;
};

export const ArtifactViewProvider = ({
  threadId,
  messageId,
  artifactId,
  isReadOnly,
  isSharedThread = false,
  appletPreviewSrc,
  maximized = false,
  fill = false,
  onRequestClose,
  patcherOverride,
  children,
}: {
  threadId: number;
  messageId: number;
  artifactId: string;
  isReadOnly: boolean;
  isSharedThread?: boolean;
  appletPreviewSrc?: string;
  maximized?: boolean;
  fill?: boolean;
  onRequestClose?: () => void;
  patcherOverride?: ArtifactViewPatcherOverride;
  children: ReactNode;
}) => {
  // The default network-based patcher always exists; the override (if given)
  // wins. Hooks can't be conditional, so we always call usePatchArtifact —
  // its mutation just goes unused when an override is in play.
  const network = usePatchArtifact({ threadId, messageId, artifactId });
  const patch = patcherOverride?.patch ?? network.patch;
  const patchOne = patcherOverride?.patchOne ?? network.patchOne;
  const isPending = patcherOverride?.isPending ?? network.isPending;

  const value = useMemo(
    () => ({
      threadId,
      messageId,
      artifactId,
      isReadOnly,
      isSharedThread,
      appletPreviewSrc,
      maximized,
      fill,
      isPending,
      patch,
      patchOne,
      onRequestClose,
    }),
    [
      threadId,
      messageId,
      artifactId,
      isReadOnly,
      isSharedThread,
      appletPreviewSrc,
      maximized,
      fill,
      isPending,
      patch,
      patchOne,
      onRequestClose,
    ],
  );
  return <ArtifactViewContext.Provider value={value}>{children}</ArtifactViewContext.Provider>;
};
